import { useMemo, useState } from 'react'

import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceLegalEntitySelectorDocument,
  FinanceLegalEntitySelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { mapToEntitySelectEntity, mapToFinanceLegalEntitySelectorFragment } from './mappers'
import { FinanceLegalEntitySelectorProps, GroupType } from './types'

export const FinanceLegalEntitySelector = (props: FinanceLegalEntitySelectorProps) => {
  const {
    value,
    queryParams,
    alwaysOpen,
    defaultOpen,
    width,
    minWidth,
    showCurrency,
    currency,
    size = 'md',
    required,
    label,
  } = props

  const [skip, setSkip] = useState<boolean>(!(alwaysOpen || defaultOpen))

  const extractConnection = (data: FinanceLegalEntitySelectorQuery) => {
    return data.companies.legalEntitiesConnection
  }

  const { loading, data } = useQuery(FinanceLegalEntitySelectorDocument, {
    variables: {
      excludeIds: queryParams?.excludeIds,
      search: queryParams?.search,
    },
    skip,
  })

  const legalEntities = data ? extractConnection(data).edges.map((edge) => edge.node) : []

  const filteredLegalEntities = currency
    ? legalEntities.filter((entity) => entity.currency === currency)
    : legalEntities

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const legalEntitiesEntities = useMemo(
    () => mapToEntitySelectEntity(filteredLegalEntities, showCurrency),
    [filteredLegalEntities, showCurrency]
  )

  const selectedLegalEntities = useMemo(
    () =>
      value
        ? [
            {
              id: String(value.id),
              name: showCurrency
                ? `${value.legalName} - [${value.currency}]`
                : value.legalName || '',
            },
          ]
        : [],
    [value, showCurrency]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: legalEntitiesEntities,
    }),
    [legalEntitiesEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedLegalEntities ?? [],
    }),
    [selectedLegalEntities]
  )

  const handleOnSelect = (selection: EntitySelectEntity | null) => {
    if (filteredLegalEntities) {
      props.onSelect(
        selection ? mapToFinanceLegalEntitySelectorFragment(selection, filteredLegalEntities) : null
      )
    }
  }

  return (
    <EntitySelectWrapper
      {...props}
      value={props.value}
      label={label || i18n.t('finance.selectors.legal_entities.select_legal_entities')}
      hiddenAvatar
      loading={loading}
      onOpen={onOpen}
      entities={groupMap['all'] || []}
      selectedGroup={'all'}
      selectedGroupedEntities={selectedGroupMap['all']}
      onSelectExpandedElements={() => {}}
      expandedElements={[]}
      selectedEntities={selectedLegalEntities}
      searchPlaceholder={i18n.t('finance.selectors.legal_entities.search')}
      selectAllLabel={i18n.t('finance.selectors.legal_entities.select_all')}
      clearLabel={i18n.t('finance.selectors.legal_entities.clear')}
      selectedLabel={i18n.t('finance.selectors.legal_entities.selected')}
      notFoundSubtitle={i18n.t('finance.selectors.legal_entities.not_found.subtitle')}
      notFoundTitle={i18n.t('finance.selectors.legal_entities.not_found.title')}
      placeholder={i18n.t('finance.selectors.legal_entities.select_legal_entities')}
      selectedItemsCopy={i18n.t('finance.selectors.legal_entities.legal_entities_selected')}
      groups={[{ label: i18n.t('finance.selectors.legal_entities.groups.none'), value: 'all' }]}
      onGroupChange={() => {}}
      singleSelector
      onSelect={handleOnSelect}
      onSelectMultiple={() => {}}
      minWidth={minWidth}
      width={width}
      size={size}
      required={required}
    />
  )
}
